import { useState, useEffect, useContext } from "react";
import React from "react";
import {
  SvgArrowRight,
  SvgCamera,
  SvgToastSuccessful,
  SvgNvr,
  SvgToastInfo,
  SvgBoxUnChecked,
  SvgBoxIndeterminateChecked,
  SvgBoxChecked,
} from "ui/svgs";
import { showToast } from "ui/components/common";
import { AuthContext } from "context/AuthContext";
import useSWR from "swr";
import { fetchWithParams } from "lib/fetcher";
import { getSiteCameras } from "pages/service";
import { CheckState } from "lib/constants";
import { CameraInfo } from "lib/types";
import { useParams } from "react-router-dom";

export default function NvrItem({
  siteId,
  objData,
  defaultNvrId,
  selectedCameras,
  setSelectedCameras,
  nvrInitalErrors,
  cameraInitalErrors,
  initialSuccessCameraIds,
}: {
  siteId: string;
  objData: any;
  defaultNvrId: number;
  selectedCameras: { cameraId: number; name: string }[];
  nvrInitalErrors: { nvrId: number; error: string }[];
  cameraInitalErrors: { cameraId: number; error: string }[];
  initialSuccessCameraIds: string[];
  setSelectedCameras: Function;
}) {
  const [active, setActive] = useState(false);
  const [height, setHeight] = useState("0px");
  const [cameras, setCameras] = useState<CameraInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [batchCheckState, setBatchCheckState] = useState<CheckState>(
    defaultNvrId === objData?.id
      ? CheckState.indeterminate
      : CheckState.unchecked,
  );
  const refContent = React.useRef<HTMLDivElement>(null);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const error = cameraInitalErrors.some((item) => {
      if (cameras?.findIndex((camera) => camera.id === item.cameraId) !== -1) {
        return true;
      }
      return false;
    });
    if (!error) {
      return;
    }
    if (!active) {
      toggleAccordion();
    } else {
      setHeight(!active ? "0px" : `${refContent.current.scrollHeight}px`);
    }
  }, [nvrInitalErrors, cameraInitalErrors]);

  function toggleAccordion() {
    setActive(!active);
    if (refContent.current != null && refContent.current != undefined) {
      console.log(
        "toggleAccordion scrollHeight = ",
        refContent.current.scrollHeight,
      );
      setHeight(active ? "0px" : `${refContent.current.scrollHeight}px`);
    }
  }

  const loadCameraList = async () => {
    setIsLoading(true);
    const data = await getSiteCameras(user.orgId, +siteId, objData.id);
    setIsLoading(false);
    if (data?.status !== 200) {
      showToast(1, "Failed to get camera list, please try again later");
      return;
    }

    setCameras(data?.data?.data);
  };

  const rotate = active ? "rotate(90deg)" : "rotate(0)";
  const handleClick = async () => {
    if (cameras?.length > 0) {
      toggleAccordion();
    } else {
      await loadCameraList();
      setTimeout(() => toggleAccordion(), 200);
    }
  };

  useEffect(() => {
    if (!cameras || cameras.length === 0) {
      return;
    }
    let newSelectedCameras = [];
    if (batchCheckState === CheckState.unchecked) {
      newSelectedCameras = selectedCameras.filter(
        (camera) =>
          cameras?.findIndex((item) => item.id === camera.cameraId) === -1,
      );
      setSelectedCameras(newSelectedCameras);
    } else if (batchCheckState === CheckState.checked) {
      const addedCameras = cameras
        .filter(
          (camera) =>
            selectedCameras.findIndex((item) => item.cameraId === camera.id) ===
            -1,
        )
        .map((camera) => {
          return { cameraId: camera.id, name: camera.name };
        });

      newSelectedCameras = [...selectedCameras, ...addedCameras];
      setSelectedCameras(newSelectedCameras);
    }

    if (cameras?.length === newSelectedCameras?.length) {
      setBatchCheckState(CheckState.checked);
    }
  }, [cameras, batchCheckState]);

  const handleNvrCheck = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (cameras?.length === 0) {
      await loadCameraList();
    }
    if (batchCheckState === CheckState.checked) {
      setBatchCheckState(CheckState.unchecked);
    } else {
      setBatchCheckState(CheckState.checked);
    }
  };

  const handleCameraCheck = (cameraId: number) => {
    let newSelectedCameras;
    if (
      selectedCameras.findIndex((item) => item.cameraId === cameraId) !== -1
    ) {
      newSelectedCameras = selectedCameras.filter(
        (camera) => camera.cameraId !== cameraId,
      );
      setSelectedCameras(newSelectedCameras);
    } else {
      const camera = cameras?.find((camera) => camera.id === cameraId);
      newSelectedCameras = [
        ...selectedCameras,
        { cameraId: cameraId, name: camera.name },
      ];
      setSelectedCameras(newSelectedCameras);
    }
    const camerasInSelects = newSelectedCameras.filter(
      (camera) =>
        cameras?.findIndex((item) => item.id === camera.cameraId) >= 0,
    );
    if (camerasInSelects?.length === 0) {
      setBatchCheckState(CheckState.unchecked);
    } else if (camerasInSelects?.length === cameras?.length) {
      setBatchCheckState(CheckState.checked);
    } else {
      setBatchCheckState(CheckState.indeterminate);
    }
  };

  return (
    <>
      <div
        className="py-3 w-full cursor-pointer flex justify-between gap-1 text-center items-center"
        onClick={handleClick}
      >
        <div className="flex items-center max-w-[90%]">
          <div className="mr-1" onClick={handleNvrCheck}>
            {batchCheckState === CheckState.checked ? (
              <SvgBoxChecked width="18" className="fill-neutral-500 pr-1" />
            ) : batchCheckState === CheckState.indeterminate ? (
              <SvgBoxIndeterminateChecked
                width="18"
                className="fill-neutral-500 pr-1"
              />
            ) : (
              <SvgBoxUnChecked width="18" className="fill-neutral-500 pr-1" />
            )}
          </div>

          <SvgArrowRight
            width="22"
            style={{ transform: rotate, transition: "all 0.2s linear" }}
            className="fill-neutral-500 mr-1 flex-shrink-0"
          />

          <span className="font-semibold mr-4 max-w-full overflow-hidden break-words text-nowrap text-ellipsis">
            <SvgNvr width="22" className="fill-neutral-500 inline-block mr-2" />
            {objData.name}
          </span>
          {isLoading && (
            <div
              className="text-blue-500 ml-auto inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            ></div>
          )}
        </div>
      </div>
      {nvrInitalErrors.findIndex((item) => item.nvrId === objData.id) !==
        -1 && (
        <div className="w-full flex px-6">
          <SvgToastInfo width="16" className="fill-[#F73D2F] inline-block" />
          <span className="ml-2 text-[#F73D2F] overflow-hidden break-words text-nowrap text-ellipsis max-w-[90%]">
            {nvrInitalErrors.find((item) => item.nvrId === objData.id).error}
          </span>
        </div>
      )}

      <div
        ref={refContent}
        className="overflow-hidden block"
        style={{ maxHeight: `${height}`, transition: "max-height 0.4s ease" }}
      >
        {cameras.length && (
          <>
            {cameras?.map((camera: any) => (
              <div key={camera.id} className="pl-[22px] pt-3.5">
                <div className="flex max-w-[90%] items-center gap-2">
                  <button
                    onClick={() => {
                      handleCameraCheck(camera.id);
                    }}
                  >
                    {selectedCameras.findIndex(
                      (item) => item.cameraId === camera.id,
                    ) !== -1 ? (
                      <SvgBoxChecked
                        width="18"
                        className="fill-neutral-500 pr-1"
                      />
                    ) : (
                      <SvgBoxUnChecked
                        width="18"
                        className="fill-neutral-500 pr-1"
                      />
                    )}
                  </button>
                  <SvgCamera
                    width="19"
                    className="fill-neutral-500 inline-block flex-shrink-0"
                  />
                  <span className="font-semibold overflow-hidden break-words text-nowrap text-ellipsis">
                    {camera.name}
                  </span>
                </div>
                {cameraInitalErrors.findIndex(
                  (item) => item.cameraId === camera.id,
                ) !== -1 ? (
                  <div className="w-full flex px-7 pt-2">
                    <SvgToastInfo
                      width="16"
                      className="fill-[#F73D2F] inline-block"
                    />
                    <span className="ml-2 text-[#F73D2F] overflow-hidden break-words text-nowrap text-ellipsis max-w-[90%]">
                      {
                        cameraInitalErrors.find(
                          (item) => item.cameraId === camera.id,
                        )?.error
                      }
                    </span>
                  </div>
                ) : initialSuccessCameraIds.includes(camera.id) ? (
                  <div className="w-full flex px-7 pt-2">
                    <SvgToastSuccessful
                      width="16"
                      className="fill-[#3A855D] inline-block"
                    />
                    <span className="ml-2 text-[#3A855D]">
                      Playback download has begun and is in progress.
                    </span>
                  </div>
                ) : (
                  <div className="h-3.5" />
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}
